<script>
/**
 * Mixin for filter and sorting options of GW stats
 *
 * Override list:
 *    componentsListRefs
 *    gwStatsOptConfigChanged
 *    gwStatsOptClItems
 *    gwStatsOptZoneAll
 *    gwStatsOptZoneFilter
 *    gwStatsOptTeamAll
 *    gwStatsOptTeamFilter
 *    gwStatsOptChecklistAll
 *    gwStatsOptChecklistFilter
 *    gwStatsOptTemplateAll
 *    gwStatsOptTemplateFilter
 *    gwStatsOptConfigChanged (method ; optional)
 */

import { isEqual, sortBy, uniq } from 'lodash';
import { mapState, createNamespacedHelpers } from 'vuex';
import { GET_ZONES } from '@/store/mutation-types';
import { FETCH_ZONES } from '@/store/action-types';
import { GET_GW_TEMPLATES, GET_GW_CHECKLISTS } from '@/store/modules/gemba-walks/mutation-types';
import { FETCH_GW_TEMPLATES, FETCH_GW_CHECKLISTS } from '@/store/modules/gemba-walks/action-types';

const { mapState: gwMapState, mapActions: gwMapActions } = createNamespacedHelpers('gembaWalks');

export default {

  data() {
    return {
      gwStatsOptTmpRefreshTimeout: null,
    };
  },

  computed: {
    gwStatsOptions() {
      return [
        {
          key: 'displayTitle',
          component: 'h2',
          options: {
            class: 'basic-sub-title',
          },
          slots: [{
            component: 'span',
            content: this.$t('message.DISPLAY_NOUN'),
          }],
        },
        {
          key: 'defaultPeriod',
          value: this.gwStatsOptDefaultPresetId,
          component: 'IndicatorDisplayConfigField',
          options: {
            class: 'mt5em',
          },
        },
        {
          key: 'hideNonWD',
          component: 'IntCheckbox',
          value: this.gwStatsHideNonWD,
          options: {
            class: 'mt5',
            title: this.$t('message.GW_HIDE_WD'),
          },
        },
        {
          key: 'hideNonWDHint',
          component: 'div',
          options: {
            class: 'mb5 field-hint',
          },
          slots: [{
            component: 'span',
            content: this.$tc('message.GW_CUMULATED_NON_WD', this.gwStatsNonWDNumber, { nb: this.gwStatsNonWDNumber }),
          }],
        },
        {
          key: 'filterTitle',
          component: 'h2',
          options: {
            class: 'basic-sub-title',
          },
          slots: [{
            component: 'span',
            content: this.$t('message.FILTERS'),
          }],
        },
        {
          key: 'clItemsTitle',
          component: 'div',
          options: {
            class: 'mt5 mb5 legend',
          },
          slots: [{
            component: 'span',
            content: this.$t('message.GW_CL_ITEMS'),
          }],
        },
        {
          key: 'clItems',
          component: 'CheckboxList',
          value: this.gwStatsOptClItems,
          options: {
            items: this.gwStatsOptClAllOptions,
          },
        },
        // Locations/Zones
        {
          key: 'zoneTitle',
          component: 'div',
          options: {
            class: 'mt5 mb5 legend',
          },
          slots: [{
            component: 'span',
            content: this.$t('message.LOCATIONS'),
          }],
        },
        {
          key: 'zoneAll',
          value: this.gwStatsOptZoneAll,
          component: 'RadioGroup',
          options: {
            items: [
              { id: 0, label: this.$t('message.ALLS') },
              { id: 1, label: this.$t('message.GW_PICK_LOCATION') },
            ],
          },
          on: {
            input: (filter) => {
              if (filter === 1) {
                this.componentsListRefs.zoneFilter.edit();
              }
            },
          },
        },
        {
          key: 'zoneFilter',
          value: this.gwStatsOptZoneFilter,
          component: 'TreeField',
          options: {
            ref: 'zoneFilter',
            class: 'gw-stats-filter',
            style: { display: this.gwStatsOptZoneAll === 1 ? undefined : 'none' },
            placeholder: this.$t('message.NONE'),
            items: this.gwStatsOptZonesData,
          },
          callback: (option, next) => {
            option.value = Number(option.value);
            next(option);
          },
          on: {
            close: () => {
              this.gwStatsOptConfigChanged({ key: 'zoneAll', value: 0 });
            },
            ok: () => {
              this.componentsListRefs.zoneFilter.stopEdit();
            },
          },
        },
        // Audited teams
        {
          key: 'teamTitle',
          component: 'div',
          options: {
            class: 'mt5 mb5 legend',
          },
          slots: [{
            component: 'span',
            content: this.$t('message.GW_AUDITED_TEAMS'),
          }],
        },
        {
          key: 'teamAll',
          value: this.gwStatsOptTeamAll,
          component: 'RadioGroup',
          options: {
            items: [
              { id: 0, label: this.$t('message.ALLS') },
              { id: 1, label: this.$t('message.GW_PICK_TEAM') },
            ],
          },
          on: {
            input: (filter) => {
              if (filter === 1) {
                this.componentsListRefs.teamFilter.edit();
              }
            },
          },
        },
        {
          key: 'teamFilter',
          component: 'AdvancedTeamPicker',
          value: this.gwStatsOptTeamFilter,
          options: {
            ref: 'teamFilter',
            class: 'gw-stats-filter',
            placeholder: this.$t('message.NONE_FEM'),
            invisible: this.gwStatsOptTeamAll !== 1,
            legend: '',
          },
          on: {
            input: (teams) => {
              if (!teams || !teams.length) {
                this.gwStatsOptConfigChanged({ key: 'teamAll', value: 0 });
              }
            },
          },
        },
        // Audited users
        {
          key: 'gwStatsAuditedUsersTitle',
          component: 'div',
          options: {
            class: 'mt5 mb5 legend',
          },
          slots: [{
            component: 'span',
            content: this.$t('message.GW_AUDITED_USERS'),
          }],
        },
        {
          key: 'gwStatsAuditedUsersAll',
          value: this.gwStatsOptUsersAll,
          component: 'RadioGroup',
          options: {
            items: [
              { id: 0, label: this.$t('message.ALLS') },
              { id: 1, label: this.$t('message.GW_PICK_USER') },
            ],
          },
          on: {
            input: (filter) => {
              if (filter === 1) {
                this.componentsListRefs.gwStatsAuditedUsers.edit();
              }
            },
          },
        },
        {
          key: 'gwStatsAuditedUsers',
          component: 'EditableLabelRadio',
          value: this.gwStatsOptAuditedUsers,
          options: {
            ref: 'gwStatsAuditedUsers',
            class: 'gw-stats-filter',
            multiple: true,
            items: this.gwStatsOptAuditedUsersItems,
            placeholder: this.$t('message.NO_SELECTION'),
            style: { display: this.gwStatsOptUsersAll === 1 ? undefined : 'none' },
          },
          on: {
            input: (users) => {
              if (!users || !users.length) {
                this.gwStatsOptConfigChanged({ key: 'gwStatsAuditedUsersAll', value: 0 });
              }
            },
          },
        },
        // Checklist
        {
          key: 'checklistTitle',
          component: 'div',
          options: {
            class: 'mt5 mb5 legend',
          },
          slots: [{
            component: 'span',
            content: this.$t('message.GW_CHECKLISTS'),
          }],
        },
        {
          key: 'checklistAll',
          value: this.gwStatsOptChecklistAll,
          component: 'RadioGroup',
          options: {
            items: [
              { id: 0, label: this.$t('message.ALLS') },
              { id: 1, label: this.$t('message.GW_PICK_CHECKLIST') },
            ],
          },
          on: {
            input: (filter) => {
              if (filter === 1) {
                this.componentsListRefs.checklistFilter.edit();
              }
            },
          },
        },
        {
          key: 'checklistFilter',
          value: this.gwStatsOptChecklistFilter,
          component: 'EditableLabelRadio',
          options: {
            ref: 'checklistFilter',
            class: 'gw-stats-filter',
            style: { display: this.gwStatsOptChecklistAll === 1 ? undefined : 'none' },
            placeholder: this.$t('message.ALL'),
            labelKey: 'name',
            items: this.gwStatsOptChecklistsData,
            multiple: true,
            separator: ', ',
            okButton: true,
          },
          on: {
            close: () => {
              if (!this.gwStatsOptChecklistFilter || !this.gwStatsOptChecklistFilter.length) {
                this.gwStatsOptConfigChanged({ key: 'checklistAll', value: 0 });
              }
            },
            ok: () => {
              this.componentsListRefs.checklistFilter.stopEdit();
              if (!this.gwStatsOptChecklistFilter || !this.gwStatsOptChecklistFilter.length) {
                this.gwStatsOptConfigChanged({ key: 'checklistAll', value: 0 });
              }
            },
          },
        },
        // Templates
        {
          key: 'templateTitle',
          component: 'div',
          options: {
            class: 'mt5 mb5 legend',
          },
          slots: [{
            component: 'span',
            content: this.$t('message.GW_TEMPLATES_SHORT'),
          }],
        },
        {
          key: 'templateAll',
          value: this.gwStatsOptTemplateAll,
          component: 'RadioGroup',
          options: {
            items: [
              { id: 0, label: this.$t('message.ALLS') },
              { id: 1, label: this.$t('message.GW_PICK_TEMPLATE') },
            ],
          },
          on: {
            input: (filter) => {
              if (filter === 1) {
                this.componentsListRefs.templateFilter.edit();
              }
            },
          },
        },
        {
          key: 'templateFilter',
          value: this.gwStatsOptTemplateFilter,
          component: 'EditableLabelRadio',
          options: {
            ref: 'templateFilter',
            class: 'gw-stats-filter',
            style: { display: this.gwStatsOptTemplateAll === 1 ? undefined : 'none' },
            placeholder: this.$t('message.ALL'),
            labelKey: 'name',
            items: this.gwStatsOptTemplatesRadioItems,
            multiple: true,
            separator: ', ',
            okButton: true,
          },
          on: {
            close: () => {
              if (!this.gwStatsOptTemplateFilter || !this.gwStatsOptTemplateFilter.length) {
                this.gwStatsOptConfigChanged({ key: 'templateAll', value: 0 });
              }
            },
            ok: () => {
              this.componentsListRefs.templateFilter.stopEdit();
              if (!this.gwStatsOptTemplateFilter || !this.gwStatsOptTemplateFilter.length) {
                this.gwStatsOptConfigChanged({ key: 'templateAll', value: 0 });
              }
            },
          },
        },
      ];
    },
    gwStatsHideNonWD() {
      if (this.allPageOptions && !isNaN(this.allPageOptions.hideNonWD)) {
        return this.allPageOptions.hideNonWD;
      }
      return 0;
    },
    gwStatsOptClAllOptions() {
      return [
        { id: 0, label: this.$t('message.GW_NOT_APPLICABLE') },
        { id: 1, label: this.$t('message.GW_NOK_ITEMS') },
        { id: 2, label: this.$t('message.GW_OK_ITEMS') },
      ];
    },
    gwStatsOptClItems() {
      if (this.allPageOptions && this.allPageOptions.clItems instanceof Array) {
        return this.allPageOptions.clItems;
      }
      return this.gwStatsOptClAllOptions.map(o => o.id);
    },
    gwStatsOptZoneAll() {
      if (this.allPageOptions && this.allPageOptions.zoneAll !== undefined) {
        return Number(this.allPageOptions.zoneAll);
      }
      return 0;
    },
    gwStatsOptZoneFilter() {
      if (this.allPageOptions && this.allPageOptions.zoneFilter !== undefined) {
        return this.allPageOptions.zoneFilter;
      }
      return 0;
    },
    gwStatsOptTeamAll() {
      if (this.allPageOptions && this.allPageOptions.teamAll !== undefined) {
        return Number(this.allPageOptions.teamAll);
      }
      return 0;
    },
    gwStatsOptTeamFilter() {
      if (this.allPageOptions && this.allPageOptions.teamFilter instanceof Array) {
        return this.allPageOptions.teamFilter;
      }
      if (this.gwStatsOptTeamAll === 0) {
        return this.$store.getters.teamsTreeCurrentFlattened.map(team => team.id);
      }
      return [];
    },
    gwStatsOptAuditedUsersItems() {
      // Users of the supervised teams
      return this.$store.getters.teamsTreeCurrentFlattened
        .map(team => ({
          ...team,
          radioItems: team.users,
        }));
    },
    gwStatsOptUsersAll() {
      if (this.allPageOptions && this.allPageOptions.gwStatsAuditedUsersAll !== undefined) {
        return Number(this.allPageOptions.gwStatsAuditedUsersAll);
      }
      return 0;
    },
    gwStatsOptAuditedUsers() {
      if (this.allPageOptions && this.allPageOptions.gwStatsAuditedUsers instanceof Array) {
        return this.allPageOptions.gwStatsAuditedUsers;
      }
      if (this.gwStatsOptUsersAll === 0) {
        return uniq(this.$store.getters.teamsTreeCurrentFlattened.flatMap(team => team.users.map(user => user.id)));
      }
      return [];
    },
    gwStatsOptChecklistAll() {
      if (this.allPageOptions && this.allPageOptions.checklistAll !== undefined) {
        return Number(this.allPageOptions.checklistAll);
      }
      return 0;
    },
    gwStatsOptChecklistFilter() {
      if (this.allPageOptions && this.allPageOptions.checklistFilter instanceof Array) {
        return this.allPageOptions.checklistFilter;
      }
      return [];
    },
    gwStatsOptTemplateAll() {
      if (this.allPageOptions && this.allPageOptions.templateAll !== undefined) {
        return Number(this.allPageOptions.templateAll);
      }
      return 0;
    },
    gwStatsOptTemplateFilter() {
      if (this.allPageOptions && this.allPageOptions.templateFilter instanceof Array) {
        return this.allPageOptions.templateFilter;
      }
      return [];
    },
    gwStatsOptDefaultPresetId() {
      if (this.allPageOptions && !isNaN(this.allPageOptions.defaultPeriod)) {
        return this.allPageOptions.defaultPeriod;
      }
      return 5;
    },
    /** Object: All GW templates per team id (including those of other teams) */
    gwStatsOptTemplatesData() {
      return this.$store.state.gembaWalks.templatesPerTeam;
    },
    /** Array: Items selectable in the templates filter */
    gwStatsOptTemplatesRadioItems() {
      return this.$store.getters.teamsTreeCurrentFlattened
        .filter(team => this.gwStatsOptTeamFilter.includes(team.id))
        .map(team => ({
          ...team,
          title: team.name,
          radioItems: this.gwStatsOptTemplatesData[`${team.id}`],
        }))
        .filter(team => team.radioItems && team.radioItems.length > 0);
    },
    componentsListRefs() {
      // This computed needs to be overrided
      return null;
    },
    ...mapState({
      gwStatsOptZonesData: state => state[GET_ZONES.stateKey] || [],
      gwStatsOptZonesLoading: state => state[GET_ZONES.loadingKey],
    }),
    ...gwMapState({
      gwStatsOptChecklistsData: state => state[GET_GW_CHECKLISTS.stateKey] || [],
      gwStatsOptChecklistsLoading: state => state[GET_GW_CHECKLISTS.loadingKey],
      gwTemplatesLoading: state => state[GET_GW_TEMPLATES.loadingKey],
    }),
  },

  watch: {
    gwStatsOptTeamFilter(newVal, oldVal) {
      if (!isEqual(sortBy(newVal), sortBy(oldVal))) {
        clearTimeout(this.gwStatsOptTmpRefreshTimeout);
        this.gwStatsOptTmpRefreshTimeout = setTimeout(this.gwStatsOptRefreshTemplates, 150);
      }
    },
  },

  created() {
    if (!this.gwStatsOptZonesLoading && this.currentTeam) {
      this.$store.dispatch(FETCH_ZONES);
    }
    if (!this.gwStatsOptChecklistsLoading && this.currentTeam) {
      this.FETCH_GW_CHECKLISTS();
    }
    this.gwStatsOptRefreshTemplates();
  },

  methods: {
    gwStatsOptRefreshTemplates() {
      // TODO Refresh templates according to selected teams
      // TODO: In templates radio, add level for each team
      if (this.currentTeam && this.currentTeam.id && !this.gwTemplatesLoading) {
        this.FETCH_GW_TEMPLATES({ teams: this.gwStatsOptTeamFilter.length ? this.gwStatsOptTeamFilter : undefined });
      }
    },
    gwStatsOptConfigChanged(option) {
      this.$emit('configChange', option);
    },
    gwStatsOptQueryParams(options) {
      if (!options) {
        return '';
      }
      let query = '';
      let amp = '';
      if (options.defaultPeriod !== undefined) {
        query += `${amp}defaultPeriod=${options.defaultPeriod}`;
        amp = '&';
      }
      if (options.hideNonWD !== undefined) {
        query += `hideNonWD=${options.hideNonWD}`;
        amp = '&';
      }
      if (options.clItems) {
        query += `${amp}clItems=${options.clItems.join(',')}`;
        amp = '&';
      }
      if (options.zoneFilter && options.zoneFilter.length && options.zoneAll !== 0) {
        query += `${amp}zoneFilter=${options.zoneFilter.join(',')}`;
        amp = '&';
      }
      if (options.teamFilter && options.teamFilter.length && options.teamAll !== 0) {
        query += `${amp}teamFilter=${options.teamFilter.join(',')}`;
        amp = '&';
      }
      if (options.gwStatsAuditedUsers && options.gwStatsAuditedUsers.length && options.gwStatsOptUsersAll !== 0) {
        query += `${amp}gwStatsAuditedUsers=${options.gwStatsAuditedUsers.join(',')}`;
        amp = '&';
      }
      if (options.checklistFilter && options.checklistFilter.length && options.checklistAll !== 0) {
        query += `${amp}checklistFilter=${options.checklistFilter.join(',')}`;
        amp = '&';
      }
      if (options.templateFilter && options.templateFilter.length && options.templateAll !== 0) {
        query += `${amp}templateFilter=${options.templateFilter.join(',')}`;
        amp = '&';
      }
      return query;
    },
    ...gwMapActions({
      FETCH_GW_TEMPLATES,
      FETCH_GW_CHECKLISTS,
    }),
  },
};
</script>
